import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type BrandingSettingsResult } from '@whinerp/client';
import {
  Button,
  CapabilityUnavailable,
  ErrorState,
  Field,
  InlineAction,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Setup > Branding (F-015).
 *
 * One colour in, a whole palette out, shown immediately. The preview is the
 * feature: a hex field on its own tells somebody nothing about whether their
 * brand blue will be readable as link text, and that is precisely the thing
 * that goes wrong.
 *
 * The server refuses a colour too pale to be seen and names the nearest shade
 * that works. That shade arrives as a value on the error, so this offers it as
 * one click rather than asking somebody to read it out of a sentence.
 */
export function BrandingPanel(): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const entitlement = useEntitlement('F-015');

  const data = useQuery<BrandingSettingsResult, ApiError>({
    queryKey: ['branding'],
    queryFn: () => api.brandingSettings({}),
    enabled: entitlement.state === 'on',
  });

  const [enabled, setEnabled] = useState(false);
  const [colour, setColour] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [loginBackgroundUrl, setLoginBackgroundUrl] = useState('');
  const [sendingDomain, setSendingDomain] = useState('');

  const settings = data.data;
  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.enabled);
    setColour(settings.accentColour ?? '');
    setLogoUrl(settings.logoUrl ?? '');
    setFaviconUrl(settings.faviconUrl ?? '');
    setLoginBackgroundUrl(settings.loginBackgroundUrl ?? '');
    setSendingDomain(settings.sendingDomain ?? '');
  }, [settings]);

  const save = useMutation<BrandingSettingsResult, ApiError, string>({
    mutationFn: (accent: string) =>
      api.saveBrandingSettings({
        body: {
          enabled,
          accentColour: accent,
          logoUrl,
          faviconUrl,
          loginBackgroundUrl,
          sendingDomain,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['branding'] });
    },
  });

  if (entitlement.state === 'off' || entitlement.state === 'plan-locked') {
    return (
      <Panel data-region="branding" id="branding">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('brand.title')}</SectionLabel>
          <CapabilityUnavailable
            kind={entitlement.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('brand.title')}
            title={
              entitlement.state === 'off'
                ? t('capability.moduleOff.title')
                : t('capability.planLocked.title')
            }
            body={
              entitlement.state === 'off'
                ? t('capability.moduleOff.body', { values: { capability: t('brand.title') } })
                : t('capability.planLocked.body', {
                    values: {
                      plan: entitlement.entry?.planMinLabel ?? '',
                      current: entitlement.entry?.planMinLabel ?? '',
                    },
                  })
            }
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }

  if (data.isPending) return <Skeleton className="h-block-md w-full" />;
  if (data.isError) {
    return <ErrorState title={t('state.error')} body={data.error.message} onRetry={() => void data.refetch()} />;
  }

  const palette = data.data.palette;
  /*
   * The shade the server offered when it refused. It travels as a value on the
   * error so this can offer it as a button rather than as prose to retype.
   */
  const suggested = save.isError ? (save.error.messageValues?.['suggestion'] ?? null) : null;

  return (
    <Panel data-region="branding" id="branding">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('brand.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('brand.body')}</p>

        <label className="flex items-baseline gap-2 text-body text-ink">
          <input
            type="checkbox"
            data-brand-enabled
            checked={enabled}
            onChange={(event) => setEnabled(event.currentTarget.checked)}
          />
          {t('brand.enable')}
        </label>

        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('brand.colour')}
            data-brand-colour
            value={colour}
            onChange={(event) => setColour(event.currentTarget.value)}
          />
          {/* The colour itself, so a hex value is never the only feedback. */}
          <span
            data-brand-swatch={palette?.base ?? ''}
            aria-hidden
            className="inline-block h-icon w-icon rounded-chip border border-control"
            style={palette ? { background: palette.base } : undefined}
          />
        </div>

        <Field label={t('brand.logo')} data-brand-logo value={logoUrl} onChange={(e) => setLogoUrl(e.currentTarget.value)} />
        <Field label={t('brand.favicon')} data-brand-favicon value={faviconUrl} onChange={(e) => setFaviconUrl(e.currentTarget.value)} />
        <Field
          label={t('brand.loginBackground')}
          data-brand-login-background
          value={loginBackgroundUrl}
          onChange={(e) => setLoginBackgroundUrl(e.currentTarget.value)}
        />
        <Field
          label={t('brand.sendingDomain')}
          data-brand-sending-domain
          hint={t('brand.sendingDomain.help')}
          value={sendingDomain}
          onChange={(e) => setSendingDomain(e.currentTarget.value)}
        />

        <div>
          <Button intent="secondary" size="small" data-brand-save busy={save.isPending} onClick={() => save.mutate(colour)}>
            {t('brand.save')}
          </Button>
        </div>

        {save.isError ? (
          <div className="flex flex-col gap-1">
            <p data-brand-refusal className="text-dense text-danger-text">
              {errorMessage(save.error)}
            </p>
            {suggested ? (
              <InlineAction
                data-brand-use-suggested={suggested}
                onClick={() => {
                  setColour(suggested);
                  save.mutate(suggested);
                }}
              >
                {t('brand.useSuggested', { values: { colour: suggested } })}
              </InlineAction>
            ) : null}
          </div>
        ) : null}

        {/*
          The preview, which is the point.
          A hex field on its own says nothing about whether a brand blue is
          readable as link text - and that is the failure this feature exists
          to prevent.
        */}
        {palette ? (
          <div data-brand-preview className="flex flex-col gap-2 rounded-card border border-rule p-3">
            <p className="text-micro uppercase tracking-label text-ink-label">{t('brand.preview')}</p>
            <div className="flex flex-wrap items-center gap-3">
              <span
                data-brand-preview-button
                className="inline-flex h-input items-center rounded-control px-3 text-dense"
                style={{ background: palette.mark, color: palette.onMark }}
              >
                {t('brand.preview.button')}
              </span>
              <span data-brand-preview-link className="text-dense underline" style={{ color: palette.text }}>
                {t('brand.preview.link')}
              </span>
              <span
                data-brand-preview-chip
                className="rounded-chip px-2 py-0.5 text-meta"
                style={{ background: palette.tint, color: palette.text }}
              >
                {t('brand.preview.record')}
              </span>
            </div>
            <p className="text-meta text-ink-meta">{t('brand.derived')}</p>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
